import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, type SortingState, useReactTable } from '@tanstack/react-table'
import { ChevronDown, ChevronDownIcon, ChevronRight, ChevronUp, HelpCircle, PinIcon, SearchIcon, SlidersHorizontalIcon, TriangleAlertIcon, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useRef, useState } from 'react'

import { createCustomSortingFn, sortAgeBrackets, useEmployeeFilters, useEmployeeFilterState } from 'components/hooks/quotes/useEmployeeFilters'
import { usePinnedEmployees } from 'components/hooks/quotes/usePinnedEmployees'
import { useKeyboardShortcuts } from 'components/hooks/useKeyboardShortcuts'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Button, Checkbox, FilterPillButton, FiltersDropdown, TextInput, Tooltip, useBreakpoint } from 'slab'
import { calculateAgeAtDate, formatMoney } from 'utils'
import { type QuotingQuoteResult } from 'utils/web_api.schemas'

import { useQuote } from '../QuoteProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { EmployeeQuoteCard } from './EmployeeQuoteCard'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'

const columnHelper = createColumnHelper<QuotingQuoteResult>()

export const QuoteEmployeeTable = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  })
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'eligible',
      desc: true
    }
  ])
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [selectedEmployee, setSelectedEmployee] = useState<QuotingQuoteResult | null>(null)
  const { breakpointIsAtLeast } = useBreakpoint()

  const {
    willEnrollEmployeeIds,
    onWillEnrollEmployeeIdsChange,
    currentQuoteResults,
    getPlanAvailabilityForEmployee,
    // getTotalPlansForEmployee,
    getMonthlyAllowanceForEmployee,
    isAllowanceAffordable,
    getMinAffordableAllowanceForEmployee,
    isLoading,
    showIneligibleEmployees,
    setShowIneligibleEmployees,
    ineligibleEmployees,
  } = useSelfServeExplorerContext()

  const {
    searchQuery,
    setSearchQuery,
    filteredStates,
    setFilteredStates,
    filteredAgeBrackets,
    setFilteredAgeBrackets,
    filteredPayTypes,
    setFilteredPayTypes,
    handleStateFilterChange,
    handleAgeBracketFilterChange,
    handlePayTypeFilterChange
  } = useEmployeeFilterState()

  const { pinnedEmployeeIds, handleEmployeePinToggle, filterAndSortEmployees } = usePinnedEmployees<QuotingQuoteResult>()
  const { shouldShowPendingPricesWarning } = useQuote()

  useKeyboardShortcuts({ searchInputRef: filterInputRef })

  const handleClearAllFilters = () => {
    setSearchQuery('')
    setFilteredStates([])
    setFilteredAgeBrackets([])
    setFilteredPayTypes([])
  }

  const handleRowClick = (employee: QuotingQuoteResult) => {
    setSelectedEmployee(employee)
  }

  const handleBackToTable = () => {
    setSelectedEmployee(null)
  }

  const employeesToShow = useMemo(() => {
    if (showIneligibleEmployees) {
      return currentQuoteResults ?? []
    }
    return currentQuoteResults?.filter(employee => employee.eligible) ?? []
  }, [currentQuoteResults, showIneligibleEmployees])

  const { employeeCounts, additionalFilters } = useEmployeeFilters(employeesToShow, {
    searchQuery,
    filteredStates,
    filteredAgeBrackets,
    filteredPayTypes,
    pinnedEmployeeIds
  })

  const { employeeCountByState, employeeCountByAgeBrackets, employeeCountByPayType } = employeeCounts

  const filteredEmployees = useMemo(() => {
    return filterAndSortEmployees(employeesToShow, searchQuery, additionalFilters)
  }, [employeesToShow, searchQuery, additionalFilters, filterAndSortEmployees])

  const customSortingFn = createCustomSortingFn<QuotingQuoteResult>(sorting, pinnedEmployeeIds)

  const subtitleText = (employee: QuotingQuoteResult) => {
    const employeeAge = calculateAgeAtDate(employee.date_of_birth, new Date())
    const isMedicareEligible = employeeAge >= 65
    // const totalPlans = getTotalPlansForEmployee(employee)
    if (!employee.eligible) {
      return 'is ineligible for benefits'
    }
    if (isMedicareEligible) {
      return 'is Medicare eligible'
    }

    // return `can choose from ${totalPlans} plans`
  }

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          const employee = info.row.original
          const isUnaffordable = !isAllowanceAffordable(employee.id)
          const minAffordableAllowance = getMinAffordableAllowanceForEmployee(employee.id)

          return (
            <div>
              <button onClick={() => handleRowClick(employee)}>
                <span className='flex items-center gap-1'>
                  <span className='sensitive'>
                    {employee.name}
                  </span>
                  {!employee.eligible && (
                    <Tooltip content='Employee is ineligible for benefits'>
                      <X className='w-4 h-4 text-red-400' />
                    </Tooltip>
                  )}
                  {isUnaffordable && (
                    <Tooltip content={`Allowance is unaffordable, minimum required is ${formatMoney(minAffordableAllowance)}`}>
                      <TriangleAlertIcon className='w-4 h-4 text-red-400' />
                    </Tooltip>
                  )}
                </span>
              </button>
              <div className='body-sm text-gray-500'>
                {subtitleText(employee)}
              </div>
            </div>
          )
        },
      }),
      columnHelper.accessor(row => getMonthlyAllowanceForEmployee(row.id), {
        id: 'monthlyAllowance',
        enableSorting: true,
        sortingFn: customSortingFn(row => getMonthlyAllowanceForEmployee(row.id)),
        header: (info) => (
          <SortableHeader<QuotingQuoteResult>
            {...info}
            headerText={shouldShowPendingPricesWarning ? 'Estimated monthly allowance' : 'Monthly allowance'}
            align='right'
          />
        ),
        cell: (info) => {
          const employee = info.row.original
          const allowance = getMonthlyAllowanceForEmployee(employee.id)
          return (
            <div className='flex items-center justify-end'>
              <Tooltip content="The monthly allowance allocated for this employee's health coverage.">
                <span className='tabular-nums'>
                  {formatMoney(allowance)}
                </span>
              </Tooltip>
            </div>
          )
        },
      }),
      columnHelper.accessor(row => willEnrollEmployeeIds.has(row.id), {
        id: 'willEnroll',
        header: () => (
          <Tooltip content='This is your best guess as to who will enroll in benefits and is only used to estimate your total monthly cost'>
            <div className='flex items-center gap-1'>
              <span>Will Enroll</span>
              <div className='cursor-help'>
                <HelpCircle className='w-4 h-4 text-gray-400' />
              </div>
            </div>
          </Tooltip>
        ),
        cell: (info) => {
          const employee = info.row.original
          const isChecked = willEnrollEmployeeIds.has(employee.id)

          return (
            <div className='flex items-center justify-center'>
              <Tooltip content='Ineligible employees cannot enroll in benefits' enabled={!employee.eligible}>
                <div>
                  <Checkbox
                    checked={isChecked}
                    disabled={!employee.eligible}
                    onChange={() => {
                      if (!employee.eligible) return
                      const newEnrolledIds = new Set(willEnrollEmployeeIds)
                      if (isChecked) {
                        newEnrolledIds.delete(employee.id)
                      } else {
                        newEnrolledIds.add(employee.id)
                      }
                      onWillEnrollEmployeeIdsChange(newEnrolledIds)
                      emitEmployerOnboardingEvent('[ER] AllowanceTool will enroll toggled', {})
                    }}
                  />
                </div>
              </Tooltip>
            </div>
          )
        },
      }),
      columnHelper.accessor('rating_area_code', {
        id: 'state',
        header: 'State',
        cell: (info) => {
          const employee = info.row.original
          return employee.rating_area_code.substring(0, 2)
        },
      }),
      columnHelper.display({
        id: 'carriers',
        header: 'Carriers',
        cell: (info) => {
          const employee = info.row.original
          const planAvailability = getPlanAvailabilityForEmployee(employee)
          const carriers = planAvailability?.carriers ?? []
          const carrierIcons = planAvailability?.carrier_icons ?? {}
          const maxCarriers = 3

          // Sort carriers to prioritize those with icons
          const sortedCarriers = [...carriers].sort((a, b) => {
            const aHasIcon = !!carrierIcons[a]
            const bHasIcon = !!carrierIcons[b]

            if (aHasIcon && !bHasIcon) return -1
            if (!aHasIcon && bHasIcon) return 1
            return a.localeCompare(b)
          })

          const displayedCarriers = sortedCarriers.slice(0, maxCarriers)
          const remainingCount = carriers.length - maxCarriers

          if (carriers.length === 0) {
            return (
              <div className='flex items-center'>
                <span className='text-xs text-gray-400 italic'>
                  No carriers available
                </span>
              </div>
            )
          }

          return (
            <div className='flex items-center space-x-2'>

              <div className='flex items-center -space-x-2.5'>
                {displayedCarriers.map((carrier, index) => {
                  const iconUrl = carrierIcons[carrier] as string | undefined
                  return (
                    <Tooltip key={index} content={carrier}>
                      <div
                        className='size-8 border border-white rounded-full flex items-center justify-center cursor-pointer'
                      >
                        {iconUrl
                          ? (
                            <img
                              src={iconUrl}
                              alt={carrier}
                              className='w-8 h-8 object-contain'
                              data-testid='carrier-icon'
                            />
                            )
                          : (
                            <span className='text-xs bg-gray-100 px-2 py-1 rounded-full'>
                              {carrier.charAt(0).toUpperCase()}
                            </span>
                            )}
                      </div>
                    </Tooltip>
                  )
                })}
              </div>

              {remainingCount > 0 && (
                <Tooltip
                  content={
                    <div className='space-y-1'>
                      {carriers.map((carrier, idx) => (
                        <div key={idx} className='text-sm'>{carrier}</div>
                      ))}
                    </div>
                    }
                >
                  <span className='text-xs text-gray-500 cursor-pointer'>
                    +{remainingCount} more
                  </span>
                </Tooltip>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor(row => row.eligible ? 1 : 0, {
        id: 'eligible',
        enableSorting: true,
        sortingFn: customSortingFn(row => row.eligible ? 1 : 0),
        header: () => null,
        cell: () => null,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const employee = info.row.original
          const isPinned = pinnedEmployeeIds.includes(employee.id)

          return (
            <div className='flex items-center gap-2'>
              <Tooltip content={isPinned ? 'Unpin employee' : 'Pin employee'}>
                <Button
                  variant='tertiary'
                  size='small'
                  onClick={() => handleEmployeePinToggle(employee.id)}
                >
                  {isPinned
                    ? (
                      <PinIcon className='w-4 h-4 text-gray-500 fill-current' />
                      )
                    : (
                      <PinIcon className='w-4 h-4 text-gray-500' />
                      )}
                </Button>
              </Tooltip>
              <Tooltip content='View plans'>
                <Button
                  variant='tertiary'
                  size='small'
                  onClick={() => {
                    handleRowClick(employee)
                    emitEmployerOnboardingEvent('[ER] AllowanceTool see employee plans clicked', {})
                  }}
                >
                  <span className='flex items-center gap-1'>
                    See plans <ChevronRight className='w-4 h-4' />
                  </span>
                </Button>
              </Tooltip>
            </div>
          )
        },
      }),
    ]
  }, [willEnrollEmployeeIds, onWillEnrollEmployeeIdsChange, pinnedEmployeeIds, handleEmployeePinToggle, getPlanAvailabilityForEmployee, isAllowanceAffordable, customSortingFn, getMonthlyAllowanceForEmployee, handleRowClick])

  const table = useReactTable({
    data: filteredEmployees,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
    },
    meta: {
      setSorting,
    },
  })

  const ineligibleEmployeesToggleButton = (
    <Button
      variant='tertiary-deemphasized'
      onClick={() => setShowIneligibleEmployees(!showIneligibleEmployees)}
      leftIcon={showIneligibleEmployees ? ChevronUp : ChevronDown}
    >
      {showIneligibleEmployees
        ? 'Hide ineligible team members'
        : `Show ${ineligibleEmployees.length} ineligible team members`}
    </Button>
  )

  return (
    <div className='min-h-[38rem]'>
      <AnimatePresence mode='wait'>
        {selectedEmployee
          ? (
            <motion.div
              key='employee-details'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className='space-y-4'
            >
              <EmployeeQuoteCard
                employeeId={selectedEmployee.id}
                onBackClick={handleBackToTable}
              />
            </motion.div>
            )
          : (
            <motion.div
              key='employee-table'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className='space-y-4'
            >
              <div className='flex flex-col gap-4 xs:flex-row xs:items-center xs:justify-between'>
                <h2 className='heading-lg text-gray-800'>Employees</h2>
                <FiltersDropdown
                  trigger={() => (
                    <Button
                      variant='secondary'
                      size='large'
                      leftIcon={SlidersHorizontalIcon}
                      rightIcon={ChevronDownIcon}
                    >
                      <div className='flex items-center gap-2'>
                        <span>Search & Filter</span>
                        {filteredStates.length +
                        filteredAgeBrackets.length +
                        filteredPayTypes.length >
                        0 && (
                          <span className='bg-thatch-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs'>
                            {filteredStates.length +
                            filteredAgeBrackets.length +
                            filteredPayTypes.length}
                          </span>
                        )}
                      </div>
                    </Button>
                  )}
                  align='end'
                  width={breakpointIsAtLeast('xs') ? 'auto' : 'full'}
                >
                  <FiltersDropdown.Filters>
                    <div className='space-y-6 xs:min-w-[26rem] p-4'>
                      <div>
                        <TextInput
                          ref={filterInputRef}
                          placeholder='Search employees...'
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          label='Find an employee'
                          leadingIcon={SearchIcon}
                        />
                      </div>

                      <div className='flex gap-2'>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <FilterPillButton
                              label='State'
                              count={filteredStates.length}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className='w-56'
                            collisionPadding={50}
                          >
                            <DropdownMenuLabel>Filter by state</DropdownMenuLabel>
                            <DropdownMenuGroup>
                              {Object.entries(employeeCountByState)
                                .sort((a, b) => a[0].localeCompare(b[0]))
                                .map(([state, count]) => (
                                  <DropdownMenuItem
                                    key={state}
                                    onSelect={(e) => e.preventDefault()}
                                    aria-label={`${state}-filter-checkbox`}
                                  >
                                    <Checkbox
                                      id={state}
                                      checked={filteredStates.includes(state)}
                                      onChange={(e) =>
                                        handleStateFilterChange(
                                          state,
                                          e.target.checked
                                        )}
                                    />
                                    <label htmlFor={state}>
                                      <span className='text-sm text-gray-800'>
                                        {state}
                                      </span>
                                      <span className='text-xs text-gray-500 ml-2'>
                                        ({count})
                                      </span>
                                    </label>
                                  </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <FilterPillButton
                              label='Age'
                              count={filteredAgeBrackets.length}
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className='w-56'
                            collisionPadding={50}
                          >
                            <DropdownMenuLabel>
                              Filter by age bracket
                            </DropdownMenuLabel>
                            <DropdownMenuGroup>
                              {Object.entries(employeeCountByAgeBrackets)
                                .sort(sortAgeBrackets)
                                .map(([ageBracket, count]) => (
                                  <DropdownMenuItem
                                    key={ageBracket}
                                    onSelect={(e) => e.preventDefault()}
                                    aria-label={`${ageBracket}-filter-checkbox`}
                                  >
                                    <Checkbox
                                      id={ageBracket}
                                      checked={filteredAgeBrackets.includes(
                                        ageBracket
                                      )}
                                      onChange={(e) =>
                                        handleAgeBracketFilterChange(
                                          ageBracket,
                                          e.target.checked
                                        )}
                                    />
                                    <label htmlFor={ageBracket}>
                                      <span className='text-sm text-gray-800'>
                                        {ageBracket}
                                      </span>
                                      <span className='text-xs text-gray-500 ml-2'>
                                        ({count})
                                      </span>
                                    </label>
                                  </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {Object.keys(employeeCountByPayType).length > 1 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <FilterPillButton
                                label='Pay Type'
                                count={filteredPayTypes.length}
                              />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              className='w-56'
                              collisionPadding={50}
                            >
                              <DropdownMenuLabel>
                                Filter by pay type
                              </DropdownMenuLabel>
                              <DropdownMenuGroup>
                                {Object.entries(employeeCountByPayType).map(
                                  ([payType, count]) => (
                                    <DropdownMenuItem
                                      key={payType}
                                      onSelect={(e) => e.preventDefault()}
                                      aria-label={`${payType}-filter-checkbox`}
                                    >
                                      <Checkbox
                                        id={payType}
                                        checked={filteredPayTypes.includes(
                                          payType
                                        )}
                                        onChange={(e) =>
                                          handlePayTypeFilterChange(
                                            payType,
                                            e.target.checked
                                          )}
                                      />
                                      <label htmlFor={payType}>
                                        <span className='text-sm text-gray-800 capitalize'>
                                          {payType}
                                        </span>
                                        <span className='text-xs text-gray-500 ml-2'>
                                          ({count})
                                        </span>
                                      </label>
                                    </DropdownMenuItem>
                                  )
                                )}
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {(filteredStates.length > 0 ||
                        filteredAgeBrackets.length > 0 ||
                        filteredPayTypes.length > 0) && (
                          <Button
                            variant='tertiary-destructive'
                            onClick={handleClearAllFilters}
                          >
                            Clear all filters
                          </Button>
                        )}
                      </div>
                    </div>
                  </FiltersDropdown.Filters>
                </FiltersDropdown>
              </div>

              <TanstackTable<QuotingQuoteResult>
                table={table}
                isLoading={isLoading}
                noDataMessage='No employees found'
                paginationLabelSingular='employee'
                additionalPaginationContent={
                ineligibleEmployees.length > 0 && breakpointIsAtLeast('md')
                  ? ineligibleEmployeesToggleButton
                  : undefined
              }
              />
              {ineligibleEmployees.length > 0 && !breakpointIsAtLeast('md') && (
                <div className='text-center'>
                  {ineligibleEmployeesToggleButton}
                </div>
              )}
            </motion.div>
            )}
      </AnimatePresence>
    </div>
  )
}
